import { cn } from "@/lib/utils";
import { getAvatarColor, getAvatarInitialsFromName } from "@/lib/avatar-colors";
import { buildImageCDNUrl } from "@repo/image-transformations";
import Image from "@/components/common/image";
import { type ComponentPropsWithoutRef, forwardRef } from "react";

export type BrokerAvatarSize = "xs" | "sm" | "md" | "lg" | "xl" | "2xl";

export type BrokerAvatarProps = ComponentPropsWithoutRef<"div"> & {
  id: string;
  name: string | null | undefined;
  image?: string | null;
  isVerified?: boolean;
  showVerified?: boolean;
  size?: BrokerAvatarSize;
};

const sizeConfig: Record<
  BrokerAvatarSize,
  { container: string; fontSize: string }
> = {
  xs: { container: "size-8", fontSize: "text-xs" },
  sm: { container: "size-10", fontSize: "text-[15px]" },
  md: { container: "size-12", fontSize: "text-lg" },
  lg: { container: "size-14", fontSize: "text-[21px]" },
  xl: { container: "size-20", fontSize: "text-[30px]" },
  "2xl": { container: "size-[120px]", fontSize: "text-[44px]" },
};

export const BrokerAvatar = forwardRef<HTMLDivElement, BrokerAvatarProps>(
  function BrokerAvatar(
    {
      id,
      name,
      image,
      isVerified = false,
      showVerified = true,
      size = "md",
      className,
      ...rest
    },
    ref,
  ) {
    const hasPhoto = Boolean(image?.trim());
    const initials = getAvatarInitialsFromName(name);
    const bgColor = getAvatarColor(id);
    const { container, fontSize } = sizeConfig[size];

    const verified = showVerified && isVerified;

    return (
      <div
        ref={ref}
        className={cn(
          "relative flex shrink-0 items-center justify-center overflow-hidden rounded-full",
          container,
          verified && "ring-[2.5px] ring-white outline outline-[2px] outline-[#E8F9EF]",
          className,
        )}
        {...rest}
      >
        {hasPhoto && image ? (
          <Image
            src={buildImageCDNUrl(image, "profile")}
            alt={name ?? "Broker"}
            fill
            unoptimized
            className="object-cover"
          />
        ) : (
          <div
            className="flex h-full w-full items-center justify-center"
            style={{ backgroundColor: bgColor }}
          >
            <span
              className={cn(
                "font-display font-semibold uppercase tracking-[0.05em] text-white select-none",
                fontSize,
              )}
            >
              {initials}
            </span>
          </div>
        )}
      </div>
    );
  },
);
